import { useHandler } from "@redotech/react-util/hook";
import { plateNodesToPlainText } from "@redotech/redo-communication-common/plate-safe-content";
import type { TElement, Value } from "platejs";
import { createSlateEditor, deserializeHtml, KEYS } from "platejs";
import { usePlateEditor } from "platejs/react";
import { serializeHtml } from "platejs/static";
import { useMemo, useRef } from "react";
import { mergeAppendedValue } from "./append-value";
import {
  type EditorToolbarMode,
  FileDropPlugin,
  getBasePluginsForMode,
  getPluginsForMode,
} from "./editor-plugins";
import {
  assertEmailSafeHtml,
  convertTextNewlinesToBr,
  sanitizeEmailHtml,
} from "./email-html";
import { normalizeHtmlBlockBoundaries } from "./paste-html-normalize";
import { sanitizePlateValue } from "./plate-value-sanitize";

const createEmptyValue = (): Value => [
  { type: "p", children: [{ text: "" }] } as TElement,
];

// The one boundary that turns HTML into editor content. Callers hand it HTML
// and get a faithful value; none of them should know how Plate parses.
export function tryHtmlToPlateContent(
  html: string,
  mode: EditorToolbarMode = "email",
): Value | null {
  const editor = createSlateEditor({ plugins: getBasePluginsForMode(mode) });
  // Plate only maps `<p>` to a block — it unwraps `<div>` and turns a top-level
  // `<br>` into a newline — so rebuild the boundaries first (PRO-957).
  const nodes = deserializeHtml(editor, {
    element: normalizeHtmlBlockBoundaries(html),
  });
  return nodes.length > 0 ? sanitizePlateValue(nodes) : null;
}

export function htmlToPlateContent(
  html: string,
  mode: EditorToolbarMode = "email",
): Value {
  return tryHtmlToPlateContent(html, mode) ?? createEmptyValue();
}

function collectInlineImageUrls(value: unknown, urls: string[]): void {
  if (Array.isArray(value)) {
    for (const item of value) {
      collectInlineImageUrls(item, urls);
    }
    return;
  }
  if (value === null || typeof value !== "object") {
    return;
  }
  if (
    "type" in value &&
    value.type === KEYS.img &&
    "url" in value &&
    typeof value.url === "string" &&
    value.url.length > 0
  ) {
    urls.push(value.url as string);
  }
  if ("children" in value && Array.isArray(value.children)) {
    collectInlineImageUrls(value.children, urls);
  }
}

export interface UseMessageEditorResult {
  editor: ReturnType<typeof usePlateEditor>;
  isEmpty: boolean;
  clearContent: () => void;
  getPlainText: () => string;
  setValue: (value: Value) => void;
  /**
   * Append content after the current editor value (no-op replace when empty).
   * Used by macro/AI-assist so generated content adds to an in-progress reply
   * instead of overwriting it.
   */
  appendValue: (value: Value) => void;
  tryHtmlToPlateContent: (html: string) => Value | null;
  getSerializedValue: () => string;
  getHtml: () => Promise<string>;
  /** Whether the editor contains at least one inline image node. */
  hasInlineImages: () => boolean;
  /**
   * Point the editor's `FileDropPlugin` at the handler that should receive OS
   * file drops/pastes, or `undefined` to disable diversion (the drop falls
   * through to Slate). Pass the per-channel handler from the composer.
   */
  setFileDropHandler: (onFiles: ((files: File[]) => void) | undefined) => void;
  /** All inline image `<img>` URLs in document order (data: or http). */
  getInlineImageUrls: () => string[];
}

export function useMessageEditor(
  mode: EditorToolbarMode = "email",
  id?: string,
  initialValue?: Value,
): UseMessageEditorResult {
  // Holds the live per-channel file-drop handler. The plugin reads this at
  // drop/paste time (never during render), so we can swap the handler without
  // reconfiguring the editor or running an effect.
  const onFilesRef = useRef<((files: File[]) => void) | undefined>(undefined);
  const dispatchFileDrop = useHandler((files: File[]): boolean => {
    const handler = onFilesRef.current;
    if (!handler) {
      return false;
    }
    handler(files);
    return true;
  });

  const plugins = useMemo(
    () => [
      ...getPluginsForMode(mode),
      FileDropPlugin.configure({ options: { onFiles: dispatchFileDrop } }),
    ],
    [mode, dispatchFileDrop],
  );

  const editor = usePlateEditor({
    plugins,
    value: sanitizePlateValue(initialValue ?? createEmptyValue()),
    id: id || `message-editor-${mode}`,
  });

  const setFileDropHandler = useHandler(
    (onFiles: ((files: File[]) => void) | undefined) => {
      onFilesRef.current = onFiles;
    },
  );

  const isEmpty = editor.api.isEmpty();

  const clearContent = useHandler(() => {
    editor.tf.setValue(createEmptyValue());
  });

  const getPlainText = useHandler(() => {
    // `editor.api.string([])` drops paragraph breaks; serialize one top-level
    // block per line so newlines survive into the sent plain-text body.
    return plateNodesToPlainText(editor.children);
  });

  const setValue = useHandler((value: Value) => {
    editor.tf.setValue(value);
  });

  // Append content after whatever is already in the editor rather than
  // replacing it, so applying a macro or AI-generated reply never discards an
  // in-progress message. When the editor is empty we set the value directly to
  // avoid a leading blank paragraph.
  const appendValue = useHandler((value: Value) => {
    editor.tf.setValue(
      mergeAppendedValue(editor.children, value, editor.api.isEmpty()),
    );
  });

  const getPlateContentFromHtml = useHandler((html: string) => {
    return tryHtmlToPlateContent(html, mode);
  });

  const getSerializedValue = useHandler((): string => {
    return JSON.stringify(editor.children);
  });

  // The single boundary that turns editor content into the HTML we send as an
  // email body. Serialize, verify media/link serialization, then strip residual
  // editor bookkeeping. Every send path goes through here so the guarantees
  // hold uniformly.
  const getHtml = useHandler(async (): Promise<string> => {
    const staticEditor = createSlateEditor({
      plugins: getBasePluginsForMode(mode),
      value: editor.children,
    });
    const raw = await serializeHtml(staticEditor, {
      stripClassNames: true,
      stripDataAttributes: true,
      preserveClassNames: [],
    });
    // Preserve soft line breaks: serialized `\n` in text nodes would otherwise
    // collapse to spaces in the delivered email.
    const withLineBreaks = convertTextNewlinesToBr(raw);
    assertEmailSafeHtml(editor.children, withLineBreaks);
    return sanitizeEmailHtml(withLineBreaks);
  });

  const getInlineImageUrls = useHandler((): string[] => {
    const urls: string[] = [];
    collectInlineImageUrls(editor.children, urls);
    return urls;
  });

  const hasInlineImages = useHandler(
    (): boolean => getInlineImageUrls().length > 0,
  );

  return {
    editor,
    isEmpty,
    clearContent,
    getPlainText,
    setValue,
    appendValue,
    tryHtmlToPlateContent: getPlateContentFromHtml,
    getSerializedValue,
    getHtml,
    hasInlineImages,
    setFileDropHandler,
    getInlineImageUrls,
  };
}
